import { Check, X } from 'lucide-react';
import { DOMAIN_LABELS } from '@/api/sat';
import { CalloutPin, SatRealScreen, SurfaceFrame, type Callout } from '@/components/SatRealScreen';
import { cn } from '@/lib/utils';

/**
 * Real Screen 2 (spec 010 §5.4): a practice set with the check reveal showing.
 * The question is an example written for this page, not from the question bank.
 * Check: 3 cups of flour per 2 cups of sugar, 10 cups of sugar -> 5 x 3 = 15 cups of flour.
 */
const CALLOUTS: readonly Callout[] = [
  { title: 'Aimed at your weakest area', body: 'Each set is built from the area at the top of your results.' },
  { title: 'Check as you go', body: 'Tap Check and you see the answer and why, so a miss turns into a lesson.' },
  { title: 'A clear finish line', body: 'You always see how many questions are left in the set.' },
];

const CHOICES = [
  { key: 'A', text: '6', state: 'idle' },
  { key: 'B', text: '13', state: 'wrong' },
  { key: 'C', text: '15', state: 'right' },
  { key: 'D', text: '20', state: 'idle' },
] as const;

function PracticeSurface() {
  return (
    <SurfaceFrame url="mentaratutors.com/sat/practice">
      <div className="space-y-5 bg-background p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2.5">
            <CalloutPin n={1} />
            <p className="font-semibold">Practice set: {DOMAIN_LABELS.PROBLEM_SOLVING_AND_DATA_ANALYSIS}</p>
          </div>
          <p className="text-muted-foreground">Question 4 of 12</p>
        </div>

        <p className="text-base font-medium leading-relaxed">
          A recipe uses 3 cups of flour for every 2 cups of sugar. How many cups of flour are needed for 10 cups of
          sugar?
        </p>

        <ul className="grid gap-2.5 sm:grid-cols-2" data-testid="practice-choices">
          {CHOICES.map((c) => (
            <li
              key={c.key}
              data-state={c.state}
              className={cn(
                'flex items-center gap-3 rounded-xl border px-3.5 py-3 text-base',
                c.state === 'right' && 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30',
                c.state === 'wrong' && 'border-rose-400 bg-rose-50 dark:bg-rose-950/30',
              )}
            >
              <span
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold',
                  c.state === 'right' && 'border-emerald-500 bg-emerald-500 text-white',
                  c.state === 'wrong' && 'border-rose-500 bg-rose-500 text-white',
                )}
              >
                {c.state === 'right' ? (
                  <Check className="h-4 w-4" strokeWidth={3} aria-hidden />
                ) : c.state === 'wrong' ? (
                  <X className="h-4 w-4" strokeWidth={3} aria-hidden />
                ) : (
                  c.key
                )}
              </span>
              <span className="font-medium">{c.text}</span>
              {c.state === 'wrong' && <span className="ml-auto text-xs text-muted-foreground">Your answer</span>}
            </li>
          ))}
        </ul>

        <div className="flex items-start gap-2.5 rounded-xl border bg-muted/50 p-4">
          <CalloutPin n={2} className="mt-0.5" />
          <div className="text-sm leading-relaxed">
            <p className="font-semibold">Not quite. The answer is C, 15.</p>
            <p className="mt-1 text-muted-foreground">
              The ratio of flour to sugar is 3 to 2. 10 cups of sugar is 5 times 2, so use 5 times 3 cups of flour.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <CalloutPin n={3} />
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary" style={{ width: `${(4 / 12) * 100}%` }} />
          </div>
          <p className="shrink-0 text-xs text-muted-foreground">8 to go</p>
        </div>
      </div>
    </SurfaceFrame>
  );
}

export function SatPracticeSetScreen() {
  return (
    <SatRealScreen
      id="practice"
      title="Practice that aims"
      lead="Practice sets are built around the skills you are weakest at, so none of your time goes to what you already know."
      callouts={CALLOUTS}
      caption="Example practice set."
      layout="surface-right"
      tone="bg-muted/40 border-y"
    >
      <PracticeSurface />
    </SatRealScreen>
  );
}
